(() => {
  const mobile = window.matchMedia('(max-width: 760px)');
  let controller, observer, navSizeObserver, lastFocus, savedScroll = 0;
  const sheet = document.getElementById('topic-sheet');
  const preview = document.getElementById('preview-modal');
  const all = (selector, root = document) => [...root.querySelectorAll(selector)];
  function restorePage() {
    if (!document.body.classList.contains('dialog-open')) return;
    document.documentElement.classList.add('restoring-scroll');
    document.body.classList.remove('dialog-open');
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo({top: savedScroll, behavior: 'auto'});
    if (lastFocus?.isConnected) lastFocus.focus({preventScroll: true});
    requestAnimationFrame(() => document.documentElement.classList.remove('restoring-scroll'));
  }
  function closeDialog(dialog) {
    if (!dialog?.open) return;
    dialog.close();
    restorePage();
  }
  function openDialog(dialog, trigger) {
    if (!dialog?.showModal) return;
    lastFocus = trigger;
    if (dialog.open) { dialog.querySelector('[data-close]')?.focus(); return; }
    savedScroll = window.scrollY;
    document.body.style.width = `${document.body.getBoundingClientRect().width}px`;
    document.body.style.top = `-${savedScroll}px`;
    document.body.classList.add('dialog-open');
    dialog.showModal();
    dialog.querySelector('[data-close]')?.focus();
  }
  [sheet, preview].filter(Boolean).forEach(dialog => {
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = all('a[href], button, input, select, textarea, [tabindex]', dialog)
        .filter(item => !item.disabled && item.tabIndex >= 0 && item.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (!first) return;
      if (!dialog.contains(document.activeElement) ||
          (!event.shiftKey && document.activeElement === last) ||
          (event.shiftKey && document.activeElement === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(dialog); });
    dialog.addEventListener('close', () => {
      // Native close events are queued; a later event must not unlock a newly opened sheet.
      if (![sheet, preview].some(item => item?.open)) restorePage();
    });
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-close]')) closeDialog(dialog);
      if (event.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) closeDialog(dialog);
      }
    });
  });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('.topic-trigger');
    if (trigger && mobile.matches && sheet?.showModal) {
      const detail = sheet.querySelector('[data-topic-detail]');
      detail.replaceChildren(...[...trigger.closest('.topic-card').children].filter(n => n !== trigger).map(n => n.cloneNode(true)));
      detail.querySelector('h3').id = 'topic-sheet-title';
      openDialog(sheet, trigger);
    }
    const page = event.target.closest('[data-preview]');
    if (page && preview?.showModal) {
      event.preventDefault();
      preview.querySelector('img').src = page.href;
      preview.querySelector('img').alt = page.dataset.caption || '';
      preview.querySelector('p').textContent = page.dataset.caption || '';
      openDialog(preview, page);
    }
  });
  function init() {
    controller?.abort(); observer?.disconnect(); navSizeObserver?.disconnect();
    if (sheet?.open) closeDialog(sheet);
    controller = new AbortController();
    const options = {signal: controller.signal};
    const recognition = all('[data-recognition]');
    const roadmaps = all('[data-roadmap]');
    const recaps = all('.offer-recap-toggle');
    function selectStage(root, index, focus = false) {
      const tabs = all('[role="tab"]', root), panels = all('.roadmap-step', root);
      root.dataset.selected = index;
      tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      panels.forEach((panel, i) => {
        panel.classList.toggle('is-current', i === index);
        panel.inert = mobile.matches && i !== index;
        if (mobile.matches) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', panel.dataset.tabLabel);
          panel.setAttribute('aria-hidden', String(i !== index));
          panel.tabIndex = i === index ? 0 : -1;
        } else ['role', 'aria-labelledby', 'aria-hidden', 'tabindex'].forEach(a => panel.removeAttribute(a));
      });
      if (focus) tabs[index]?.focus();
    }
    function resize() {
      if (!mobile.matches && sheet?.open) closeDialog(sheet);
      recognition.forEach(root => {
        root.classList.add('is-enhanced');
        const expanded = root.querySelector('.recognition-toggle')?.getAttribute('aria-expanded') === 'true';
        all('[data-extra-card]', root).forEach(card => { card.hidden = mobile.matches && !expanded; });
      });
      roadmaps.forEach(root => { root.classList.add('is-enhanced'); selectStage(root, Number(root.dataset.selected || 0)); });
      all('[data-topics]').forEach(root => root.classList.toggle('is-enhanced', Boolean(sheet?.showModal)));
      recaps.forEach(button => {
        button.closest('.offer-copy').classList.add('is-enhanced');
        document.getElementById(button.getAttribute('aria-controls')).hidden = mobile.matches && button.getAttribute('aria-expanded') !== 'true';
      });
    }
    recognition.forEach(root => {
      const button = root.querySelector('.recognition-toggle');
      button?.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
        resize();
        if (!expanded) button.focus({preventScroll: true});
      }, options);
    });
    roadmaps.forEach(root => {
      const tabs = all('[role="tab"]', root);
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectStage(root, index), options);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); selectStage(root, next, true); }
        }, options);
      });
    });
    all('[data-faq]').forEach(root => {
      const buttons = all('.faq-question', root);
      const set = (button, expanded) => { button.setAttribute('aria-expanded', String(expanded)); document.getElementById(button.getAttribute('aria-controls')).hidden = !expanded; };
      buttons.forEach(button => {
        set(button, false);
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') !== 'true';
          buttons.forEach(other => set(other, other === button && expanded));
        }, options);
      });
    });
    recaps.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => { button.setAttribute('aria-expanded', String(button.getAttribute('aria-expanded') !== 'true')); resize(); }, options);
    });
    mobile.addEventListener('change', resize, options);
    resize();
    const nav = document.querySelector('[data-mobile-nav]');
    if (nav && 'ResizeObserver' in window) {
      navSizeObserver = new ResizeObserver(() => {
        const height = nav.getBoundingClientRect().height;
        if (height) document.documentElement.style.setProperty('--mobile-nav-space', `${height}px`);
      });
      navSizeObserver.observe(nav);
    }
    if (nav && 'IntersectionObserver' in window) {
      const links = all('[data-nav-anchor]', nav);
      const sections = links.map(link => ({link, target: document.querySelector(link.getAttribute('href'))}));
      sections.forEach(({link, target}) => { link.hidden = !target; });
      const update = () => {
        const available = sections.filter(s => s.target);
        const active = available.filter(s => s.target.getBoundingClientRect().top <= innerHeight * .4).at(-1) || available[0];
        links.forEach(link => { if (link === active?.link) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current'); });
      };
      observer = new IntersectionObserver(update, {rootMargin: '-5% 0px -60% 0px', threshold: 0});
      all('main > .shopify-section').forEach(section => observer.observe(section));
      update();
    }
  }
  init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => { if (sheet?.open) closeDialog(sheet); requestAnimationFrame(init); });
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest('[data-extra-card]');
    if (card?.hidden) card.closest('[data-recognition]').querySelector('.recognition-toggle')?.click();
    const stage = event.target.closest('.roadmap-step');
    if (stage && mobile.matches) document.getElementById(stage.dataset.tabLabel)?.click();
    const topic = event.target.closest('.topic-card');
    if (topic && mobile.matches) topic.querySelector('.topic-trigger')?.click();
    const faq = event.target.closest('.faq-item')?.querySelector('.faq-question');
    if (faq?.getAttribute('aria-expanded') === 'false') faq.click();
  });
})();
